// The booking calendar over the page: every "Book a demo" opens it
// (openBooking in demoBooking.js sends OPEN_BOOKING_EVENT). Mounted once in
// App. It closes on Escape, the close button or a click outside, and gives
// focus back to the link that opened it.
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import BookingFlow from "./BookingFlow";
import { trapTab } from "./a11y";
import { CloseIcon } from "./icons";
import { BOOKING_PATH, OPEN_BOOKING_EVENT } from "../demoBooking";

export default function BookingOverlay() {
  const [open, setOpen] = useState(false);
  const sheetRef = useRef(null);
  const opener = useRef(null);

  useEffect(() => {
    const onOpen = () => {
      // On the booking page the calendar is already there.
      if (window.location.pathname === BOOKING_PATH) {
        document.querySelector(".bk-page .bk")?.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      opener.current = document.activeElement;
      setOpen(true);
    };
    window.addEventListener(OPEN_BOOKING_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_BOOKING_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const scroller = document.querySelector(".routeScroll");
    const root = document.getElementById("root");
    const overflow = scroller?.style.overflow || "";
    if (scroller) scroller.style.overflow = "hidden";
    root?.setAttribute("aria-hidden", "true");
    root?.setAttribute("inert", "");
    sheetRef.current?.focus();
    return () => {
      if (scroller) scroller.style.overflow = overflow;
      root?.removeAttribute("aria-hidden");
      root?.removeAttribute("inert");
      opener.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;
  const close = () => setOpen(false);
  return createPortal(
    <div className="bk-overlay" onMouseDown={(e) => e.target === e.currentTarget && close()}>
      <div
        className="bk-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bk-title"
        tabIndex={-1}
        ref={sheetRef}
        onKeyDown={(e) => {
          if (e.key === "Escape") close();
          else trapTab(e, sheetRef.current);
        }}
      >
        <button type="button" className="bk-icon-btn bk-close" aria-label="Close" onClick={close}>
          <CloseIcon />
        </button>
        <BookingFlow onClose={close} />
      </div>
    </div>,
    document.body,
  );
}
